import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { apiClient } from '../services/api';
import BuyTokens from './BuyTokens';
import {
  CreditCardIcon,
  ChartBarIcon,
  CalendarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowUpIcon,
  ClockIcon,
  ShieldCheckIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';

const SubscriptionDetails = () => {
  const navigate = useNavigate();
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showBuyTokensModal, setShowBuyTokensModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [availablePlans, setAvailablePlans] = useState([]);

  useEffect(() => {
    loadSubscriptionData();
    loadAvailablePlans();
  }, []);

  const loadSubscriptionData = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/auth/subscription-details');
      setSubscriptionData(response.data);
    } catch (error) {
      console.error('Failed to load subscription data:', error);
      toast.error('Failed to load subscription details');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailablePlans = async () => {
    try {
      // Add cache-busting parameter to ensure fresh data
      const response = await apiClient.get(`/auth/subscription-plans?t=${Date.now()}`);
      const plans = response.data.plans || [];
      console.log('🔍 USER: Loaded subscription plans:', plans.map(p => `${p.code}: PKR ${p.price}`));
      setAvailablePlans(plans);
    } catch (error) {
      console.error('Failed to load available plans:', error);
      // Set fallback plans if API fails - exact match with database
      setAvailablePlans([
        {
          code: 'trial',
          name: 'Trial Plan',
          price: 0,
          max_invoices_per_month: 5,
          features: ['Basic invoice creation', 'FBR validation', 'Email support', '5 invoices per month'],
          is_recommended: false,
          sort_order: 1
        },
        {
          code: 'basic',
          name: 'Basic Plan',
          price: 500,
          max_invoices_per_month: 200,
          features: ['All trial features', 'FBR submission', 'Priority support', '200 invoices per month'],
          is_recommended: false,
          sort_order: 2
        },
        {
          code: 'starter',
          name: 'Starter Plan',
          price: 1000,
          max_invoices_per_month: 100,
          features: ['Basic invoice creation', 'FBR validation', 'Email support', '100 invoices per month', 'Basic templates'],
          is_recommended: false,
          sort_order: 2
        },
        {
          code: 'premium',
          name: 'Premium Plan',
          price: 5000,
          max_invoices_per_month: 1000,
          features: ['All basic features', 'Advanced analytics', 'API access', '1000 invoices per month'],
          is_recommended: true,
          sort_order: 3
        },
        {
          code: 'enterprise',
          name: 'Enterprise Plan',
          price: 10000,
          max_invoices_per_month: 999999,
          features: ['All premium features', 'Dedicated support', 'Custom integrations', 'White-label solution', 'Unlimited invoices'],
          is_recommended: false,
          sort_order: 4
        }
      ]);
    }
  };

  const handleUpgradeRequest = async () => {
    if (!selectedPlan) {
      toast.error('Please select a plan to upgrade to');
      return;
    }

    try {
      setUpgradeLoading(true);
      await apiClient.post('/auth/request-upgrade', {
        requested_plan: selectedPlan,
        current_plan: subscriptionData?.license?.type || 'trial'
      });
      
      toast.success('Upgrade request sent to administrator');
      setShowUpgradeModal(false);
      setSelectedPlan('');
      loadSubscriptionData(); // Refresh data
    } catch (error) {
      console.error('Failed to send upgrade request:', error);
      toast.error(error.response?.data?.message || 'Failed to send upgrade request');
    } finally {
      setUpgradeLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-100';
      case 'inactive': return 'text-red-600 bg-red-100';
      case 'suspended': return 'text-yellow-600 bg-yellow-100';
      case 'expired': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getUsageColor = (percentage) => {
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 75) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const formatPrice = (price) => {
    if (price === 0) return 'Free';
    return `PKR ${price.toLocaleString()}`;
  };

  const formatInvoiceLimit = (limit) => {
    if (limit >= 999999) return 'Unlimited';
    return `${limit.toLocaleString()}`;
  };

  const getCurrentPlan = () => {
    const currentPlanCode = subscriptionData?.license?.type || 'trial';
    return availablePlans.find(plan => plan.code === currentPlanCode) || {
      name: 'Trial',
      price: 0,
      max_invoices_per_month: 50,
      features: ['Basic features']
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!subscriptionData) {
    return (
      <div className="text-center py-12">
        <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">No subscription data</h3>
        <p className="mt-1 text-sm text-gray-500">Unable to load subscription information</p>
      </div>
    );
  }

  const currentPlan = getCurrentPlan();
  const usagePercentage = subscriptionData.license?.usage_percentage || 0;

  // Calculate expiry status
  const getExpiryStatus = () => {
    if (!subscriptionData.license?.expires_at) return null;
    
    const expiresAt = new Date(subscriptionData.license.expires_at);
    const now = new Date();
    const daysUntilExpiry = Math.ceil((expiresAt - now) / (1000 * 60 * 60 * 24));
    
    return { daysUntilExpiry, expiresAt };
  };

  const expiryStatus = getExpiryStatus();

  return (
    <div className="space-y-6">
      {/* Expiry Warning Banner */}
      {expiryStatus && expiryStatus.daysUntilExpiry < 30 && (
        <div className={`p-4 rounded-lg ${
          expiryStatus.daysUntilExpiry < 0
            ? 'bg-red-50 border border-red-200'
            : 'bg-yellow-50 border border-yellow-200'
        }`}>
          <div className="flex items-center">
            <ExclamationTriangleIcon className={`h-5 w-5 mr-3 ${
              expiryStatus.daysUntilExpiry < 0 ? 'text-red-600' : 'text-yellow-600'
            }`} />
            <div>
              <p className={`font-semibold ${
                expiryStatus.daysUntilExpiry < 0 ? 'text-red-800' : 'text-yellow-800'
              }`}>
                {expiryStatus.daysUntilExpiry < 0
                  ? `⚠️ Your license has expired (${expiryStatus.expiresAt.toLocaleDateString()})`
                  : `⏰ Your license expires in ${expiryStatus.daysUntilExpiry} day${expiryStatus.daysUntilExpiry !== 1 ? 's' : ''}`
                }
              </p>
              <p className={`text-sm mt-1 ${
                expiryStatus.daysUntilExpiry < 0 ? 'text-red-700' : 'text-yellow-700'
              }`}>
                {expiryStatus.daysUntilExpiry < 0
                  ? 'Invoice validation and submission are currently disabled. Please contact your administrator for renewal.'
                  : 'Renew your subscription to ensure uninterrupted access to invoice submission.'
                }
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Current Subscription Card */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <CreditCardIcon className="h-5 w-5 mr-2" />
            Current Subscription
          </h3>
        </div>
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h4 className="text-2xl font-bold text-gray-900">{currentPlan.name}</h4>
              <p className="text-gray-600">{formatPrice(currentPlan.price)}/month</p>
            </div>
            <div className="text-right">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(subscriptionData.license?.status)}`}>
                {subscriptionData.license?.status || 'inactive'}
              </span>
            </div>
          </div>

          {/* Usage Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center">
                <ChartBarIcon className="h-5 w-5 text-blue-500 mr-2" />
                <span className="text-sm font-medium text-gray-900">Monthly Usage</span>
              </div>
              <div className="mt-2">
                <div className="flex items-center justify-between text-sm">
                  <span>{subscriptionData.license?.current_usage || 0}</span>
                  <span>{subscriptionData.license?.monthly_limit || 0}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                  <div 
                    className={`h-2 rounded-full ${getUsageColor(usagePercentage)}`}
                    style={{ width: `${Math.min(usagePercentage, 100)}%` }}
                  ></div>
                </div>
                <p className="text-xs text-gray-500 mt-1">{usagePercentage}% used</p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center">
                <CalendarIcon className="h-5 w-5 text-green-500 mr-2" />
                <span className="text-sm font-medium text-gray-900">License Expires</span>
              </div>
              <div className="mt-2">
                {subscriptionData.license?.expires_at ? (
                  <>
                    <p className="text-lg font-bold text-gray-900">
                      {new Date(subscriptionData.license.expires_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                    {(() => {
                      const expiresAt = new Date(subscriptionData.license.expires_at);
                      const now = new Date();
                      const daysUntilExpiry = Math.ceil((expiresAt - now) / (1000 * 60 * 60 * 24));
                      
                      if (daysUntilExpiry < 0) {
                        return (
                          <p className="text-xs text-red-600 font-semibold mt-1">
                            ⚠️ EXPIRED - Contact administrator for renewal
                          </p>
                        );
                      } else if (daysUntilExpiry <= 30) {
                        return (
                          <p className="text-xs text-yellow-600 font-semibold mt-1">
                            ⏰ Expires in {daysUntilExpiry} day{daysUntilExpiry !== 1 ? 's' : ''}
                          </p>
                        );
                      } else {
                        return (
                          <p className="text-xs text-green-600 mt-1">
                            ✓ Active for {daysUntilExpiry} more days
                          </p>
                        );
                      }
                    })()}
                  </>
                ) : (
                  <p className="text-sm text-gray-500">Not set</p>
                )}
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center">
                <ShieldCheckIcon className="h-5 w-5 text-purple-500 mr-2" />
                <span className="text-sm font-medium text-gray-900">FBR Integration</span>
              </div>
              <div className="mt-2">
                <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                  subscriptionData.canSubmitInvoices 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {subscriptionData.canSubmitInvoices ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>

          {/* Current Plan Features */}
          <div className="mb-6">
            <h5 className="text-sm font-medium text-gray-900 mb-2">Current Plan Features</h5>
            <ul className="text-sm text-gray-600 space-y-1">
              {(currentPlan.features || []).map((feature, index) => (
                <li key={index} className="flex items-center">
                  <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2" />
                  {feature}
                </li>
              ))}
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-between items-center">
            <div>
              {usagePercentage >= 80 && (
                <div className="flex items-center text-yellow-600 text-sm">
                  <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                  You're approaching your monthly limit
                </div>
              )}
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowBuyTokensModal(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition"
              >
                <SparklesIcon className="h-4 w-4 mr-2" />
                Buy Tokens
              </button>
              <button
                onClick={() => navigate('/app/subscription')}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition"
              >
                <ArrowUpIcon className="h-4 w-4 mr-2" />
                Update Plan
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Upgrade Your Subscription</h3>
                <button
                  onClick={() => setShowUpgradeModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                {availablePlans.length === 0 ? (
                  <div className="col-span-full text-center py-8">
                    <div className="text-gray-500">
                      <CreditCardIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                      <p>Loading subscription plans...</p>
                    </div>
                  </div>
                ) : (
                  availablePlans.filter(plan => plan.is_active !== false).map((plan) => (
                    <div
                      key={plan.code}
                      className={`relative border rounded-lg p-4 cursor-pointer transition-all ${
                        selectedPlan === plan.code
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      } ${plan.is_recommended ? 'ring-2 ring-green-500' : ''}`}
                      onClick={() => setSelectedPlan(plan.code)}
                    >
                      {plan.is_recommended && (
                        <div className="absolute -top-2 left-1/2 transform -translate-x-1/2">
                          <span className="bg-green-500 text-white text-xs px-2 py-1 rounded-full">
                            Recommended
                          </span>
                        </div>
                      )}
                      <div className="text-center">
                        <h4 className="text-lg font-medium text-gray-900">{plan.name}</h4>
                        <p className="text-2xl font-bold text-gray-900 mt-2">{formatPrice(plan.price)}</p>
                        <p className="text-sm text-gray-600 mt-1">
                          {formatInvoiceLimit(plan.max_invoices_per_month)} invoices/month
                        </p>
                        <ul className="text-xs text-gray-600 mt-3 space-y-1">
                          {(plan.features || []).slice(0, 3).map((feature, index) => (
                            <li key={index}>• {feature}</li>
                          ))}
                          {plan.features && plan.features.length > 3 && (
                            <li className="text-gray-500">+{plan.features.length - 3} more</li>
                          )}
                        </ul>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowUpgradeModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpgradeRequest}
                  disabled={!selectedPlan || upgradeLoading}
                  className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {upgradeLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Sending Request...
                    </>
                  ) : (
                    <>
                      <ClockIcon className="h-4 w-4 mr-2" />
                      Request Upgrade
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Buy Tokens Modal */}
      {showBuyTokensModal && (
        <BuyTokens 
          onClose={() => setShowBuyTokensModal(false)}
          onPurchaseSuccess={() => {
            toast.success('Token purchase request submitted!');
            loadSubscriptionData();
          }}
        />
      )}
    </div>
  );
};

export default SubscriptionDetails;
